import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Users, FileText, HelpCircle, ShoppingBag, TrendingUp, 
  Plus, Trash2, Download, RefreshCw, Shield, Eye,
  Ban, CheckCircle, Clock, Zap, AlertTriangle, Globe, Edit, X,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Quill editor modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['link'],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'link'
];

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [autoFetchStatus, setAutoFetchStatus] = useState(null);
  
  // Suspend dialog
  const [suspendDialog, setSuspendDialog] = useState({ open: false, user: null });
  const [suspendReason, setSuspendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Seed database state
  const [seeding, setSeeding] = useState(false);
  
  // New content form
  const [newContent, setNewContent] = useState({
    content_type: 'article',
    title: '',
    summary: '',
    content: '',
    category: '',
    tags: '',
    image_url: '',
    video_url: '',
    read_time: 5,
    points_reward: 15
  });
  const [creating, setCreating] = useState(false);
  
  // Fetch content form
  const [fetchSource, setFetchSource] = useState('pubmed');
  const [fetchTopic, setFetchTopic] = useState('skincare');
  const [fetching, setFetching] = useState(false);
  
  // Edit content state
  const [editDialog, setEditDialog] = useState({ open: false, content: null });
  const [editContent, setEditContent] = useState({
    content_type: 'article',
    title: '',
    summary: '',
    content: '',
    category: '',
    tags: '',
    image_url: '',
    video_url: '',
    read_time: 5,
    points_reward: 15
  });
  const [editing, setEditing] = useState(false);

  // Pagination state
  const [contentPage, setContentPage] = useState(1);
  const [contentPerPage, setContentPerPage] = useState(10);
  const [contentTotal, setContentTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [usersTotal, setUsersTotal] = useState(0);

  const token = localStorage.getItem('skinuse_token');
  const headers = { 
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };

  useEffect(() => {
    if (user && user.email !== 'alghaflym@gmail.com' && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }

    if (user) {
      fetchStats();
      fetchAutoFetchStatus();
    }
  }, [user, navigate]);

  // Fetch content when pagination changes
  useEffect(() => {
    if (user && (user.email === 'alghaflym@gmail.com' || user.is_admin)) {
      fetchContent();
    }
  }, [contentPage, contentPerPage]);

  // Fetch users when pagination changes
  useEffect(() => {
    if (user && (user.email === 'alghaflym@gmail.com' || user.is_admin)) {
      fetchUsers();
    }
  }, [usersPage, usersPerPage]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/stats`, { headers });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (error) {
      console.error('Stats fetch error:', error);
    }
  };

  const fetchContent = async () => {
    setLoading(true);
    try {
      const skip = (contentPage - 1) * contentPerPage;
      const res = await fetch(`${BACKEND_URL}/api/admin/content?limit=${contentPerPage}&skip=${skip}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setContent(data.content || []);
        setContentTotal(data.total || 0);
      } else {
        console.error('Content fetch failed:', res.status);
      }
    } catch (error) {
      console.error('Content fetch error:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const skip = (usersPage - 1) * usersPerPage;
      const res = await fetch(`${BACKEND_URL}/api/admin/users?limit=${usersPerPage}&skip=${skip}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
        setUsersTotal(data.total || 0);
      } else {
        console.error('Users fetch failed:', res.status);
      }
    } catch (error) {
      console.error('Users fetch error:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchContent(), fetchUsers()]);
    setLoading(false);
  };

  const fetchAutoFetchStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auto-fetch/status`, { headers });
      if (res.ok) {
        setAutoFetchStatus(await res.json());
      }
    } catch (error) {
      console.error('Auto-fetch status error:', error);
    }
  };

  const [migratingSlug, setMigratingSlug] = useState(false);
  
  const handleMigrateSlugs = async () => {
    setMigratingSlug(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/migrate-slugs`, {
        method: 'POST',
        headers,
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || `Migrated ${data.migrated} content items to SEO-friendly URLs`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to migrate slugs');
      }
    } catch (error) {
      toast.error('Failed to migrate slugs');
    } finally {
      setMigratingSlug(false);
    }
  };

  const handleCreateContent = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const contentData = {
        ...newContent,
        tags: newContent.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      const res = await fetch(`${BACKEND_URL}/api/content`, {
        method: 'POST',
        headers,
        
        body: JSON.stringify(contentData)
      });

      if (res.ok) {
        toast.success('Content created successfully!');
        setNewContent({
          content_type: 'article',
          title: '',
          summary: '',
          content: '',
          category: '',
          tags: '',
          image_url: '',
          video_url: '',
          read_time: 5,
          points_reward: 15
        });
        fetchData();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create content');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/content/${contentId}`, {
        method: 'DELETE',
        headers,
        
      });

      if (res.ok) {
        toast.success('Content deleted');
        setContent(content.filter(c => c.content_id !== contentId));
      } else {
        throw new Error('Failed to delete content');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleOpenEditDialog = (item) => {
    setEditContent({
      content_type: item.content_type || 'article',
      title: item.title || '',
      summary: item.summary || '',
      content: item.content || '',
      category: item.category || '',
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : '',
      image_url: item.image_url || '',
      video_url: item.video_url || '',
      read_time: item.read_time || 5,
      points_reward: item.points_reward || 15
    });
    setEditDialog({ open: true, content: item });
  };

  const handleEditContent = async (e) => {
    e.preventDefault();
    if (!editDialog.content) return;
    
    setEditing(true);

    try {
      const contentData = {
        ...editContent,
        tags: editContent.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      const res = await fetch(`${BACKEND_URL}/api/content/${editDialog.content.content_id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(contentData)
      });

      if (res.ok) {
        toast.success('Content updated successfully!');
        setEditDialog({ open: false, content: null });
        fetchContent(); // Refresh the content list
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update content');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEditing(false);
    }
  };

  const handleFetchContent = async () => {
    setFetching(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/fetch-content`, {
        method: 'POST',
        headers,
        
        body: JSON.stringify({ source: fetchSource, topic: fetchTopic })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        throw new Error('Failed to fetch content');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setFetching(false);
    }
  };

  const handleTriggerAutoFetch = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auto-fetch/trigger`, {
        method: 'POST',
        headers,
        
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setTimeout(fetchAutoFetchStatus, 5000);
      }
    } catch (error) {
      toast.error('Failed to trigger auto-fetch');
    }
  };

  const handleSeedDatabase = async () => {
    if (!window.confirm('This will seed the database with all default content (videos, articles, quizzes). Continue?')) return;
    
    setSeeding(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/seed-database`, {
        method: 'POST',
        headers,
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Database seeded! Videos: ${data.videos || 0}, Articles: ${data.articles || 0}, Knowledge: ${data.knowledge || 0}, Quizzes: ${data.quizzes || 0}`);
        fetchData(); // Refresh stats
      } else {
        const error = await res.json();
        if (error.message?.includes('already seeded')) {
          toast.info(error.message);
        } else {
          throw new Error(error.detail || 'Failed to seed database');
        }
      }
    } catch (error) {
      toast.error(error.message || 'Failed to seed database');
    } finally {
      setSeeding(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!suspendDialog.user) return;
    setActionLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${suspendDialog.user.user_id}/suspend`, {
        method: 'POST',
        headers,
        
        body: JSON.stringify({ reason: suspendReason || 'Violation of terms of service' })
      });

      if (res.ok) {
        toast.success('User suspended');
        setUsers(users.map(u => 
          u.user_id === suspendDialog.user.user_id 
            ? { ...u, is_suspended: true, suspension_reason: suspendReason }
            : u
        ));
        setSuspendDialog({ open: false, user: null });
        setSuspendReason('');
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to suspend user');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspendUser = async (userId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/unsuspend`, {
        method: 'POST',
        headers,
        
      });

      if (res.ok) {
        toast.success('User unsuspended');
        setUsers(users.map(u => 
          u.user_id === userId 
            ? { ...u, is_suspended: false, suspension_reason: null }
            : u
        ));
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to unsuspend user');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers,
        
      });

      if (res.ok) {
        toast.success('User deleted');
        setUsers(users.filter(u => u.user_id !== userId));
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!user || (user.email !== 'alghaflym@gmail.com' && !user.is_admin)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="p-8 text-center">
          <Shield className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="font-serif text-2xl font-semibold mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="admin-page">
      <SEO 
        title="Admin Panel" 
        description="SkinUse Admin Dashboard - Manage content, users, and settings."
        noIndex={true}
      />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-destructive/10 rounded-sm mb-4">
                <Shield className="w-4 h-4 text-destructive" />
                <span className="text-xs font-semibold uppercase tracking-wider text-destructive">Admin Panel</span>
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl font-semibold">Dashboard</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleSeedDatabase} 
                variant="default" 
                className="gap-2 bg-gold hover:bg-gold/90 text-black"
                disabled={seeding}
                data-testid="seed-database-btn"
              >
                {seeding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {seeding ? 'Seeding...' : 'Seed Database'}
              </Button>
              <Button onClick={fetchData} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-8 flex-wrap">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="create">Create Content</TabsTrigger>
              <TabsTrigger value="fetch">Fetch Content</TabsTrigger>
              <TabsTrigger value="automation">Automation</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              {loading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <Card className="bg-card border-none shadow-card">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-sm bg-blue-100 flex items-center justify-center">
                            <Users className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-3xl font-serif font-semibold">{stats?.total_users || 0}</p>
                            <p className="text-sm text-muted-foreground">Total Users</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-card border-none shadow-card">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-sm bg-green-100 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <p className="text-3xl font-serif font-semibold">{stats?.total_content || 0}</p>
                            <p className="text-sm text-muted-foreground">Content Items</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-card border-none shadow-card">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-sm bg-purple-100 flex items-center justify-center">
                            <HelpCircle className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-3xl font-serif font-semibold">{stats?.total_quizzes || 0}</p>
                            <p className="text-sm text-muted-foreground">Quiz Questions</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-card border-none shadow-card">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-sm bg-orange-100 flex items-center justify-center">
                            <ShoppingBag className="w-6 h-6 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-3xl font-serif font-semibold">{stats?.total_deals || 0}</p>
                            <p className="text-sm text-muted-foreground">Product Deals</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Content Stats */}
                  <Card className="bg-card border-none shadow-card mb-8">
                    <CardHeader>
                      <h3 className="font-serif text-xl font-semibold flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-gold" />
                        Content Performance
                      </h3>
                    </CardHeader>
                    <CardContent>
                      <div className="grid sm:grid-cols-4 gap-4">
                        {stats?.content_stats?.map((stat) => (
                          <div key={stat._id} className="p-4 bg-secondary rounded-sm">
                            <p className="text-sm text-muted-foreground capitalize">{stat._id}</p>
                            <p className="text-2xl font-serif font-semibold">{stat.count}</p>
                            <p className="text-xs text-gold">{stat.total_views} total views</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recent Users */}
                  <Card className="bg-card border-none shadow-card">
                    <CardHeader>
                      <h3 className="font-serif text-xl font-semibold">Recent Users</h3>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {stats?.recent_users?.map((u) => (
                          <div key={u.user_id} className="flex items-center justify-between p-3 bg-secondary rounded-sm">
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                            </div>
                            <div className="text-right">
                              <Badge variant="secondary">{u.skin_points} pts</Badge>
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(u.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* Content Tab */}
            <TabsContent value="content">
              <Card className="bg-card border-none shadow-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <h3 className="font-serif text-xl font-semibold">All Content ({contentTotal})</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Show:</span>
                    <Select 
                      value={contentPerPage.toString()} 
                      onValueChange={(v) => { setContentPerPage(parseInt(v)); setContentPage(1); }}
                    >
                      <SelectTrigger className="w-20 h-8" data-testid="content-per-page-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {content.map((item) => (
                      <div key={item.content_id} className="flex items-center justify-between p-4 bg-secondary rounded-sm">
                        <div className="flex-1 min-w-0 mr-4">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">{item.content_type}</Badge>
                            <Badge variant="secondary" className="text-xs">{item.category}</Badge>
                          </div>
                          <p className="font-medium truncate">{item.title}</p>
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Eye className="w-3 h-3" /> {item.views} views
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleOpenEditDialog(item)}
                            className="text-gold hover:text-gold/80"
                            data-testid={`edit-content-${item.content_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteContent(item.content_id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-content-${item.content_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Content Pagination */}
                  {contentTotal > 0 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        Showing {((contentPage - 1) * contentPerPage) + 1} - {Math.min(contentPage * contentPerPage, contentTotal)} of {contentTotal}
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setContentPage(1)}
                          disabled={contentPage === 1}
                          data-testid="content-first-page"
                        >
                          <ChevronsLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setContentPage(p => Math.max(1, p - 1))}
                          disabled={contentPage === 1}
                          data-testid="content-prev-page"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="px-3 py-1 text-sm font-medium">
                          Page {contentPage} of {Math.ceil(contentTotal / contentPerPage)}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setContentPage(p => Math.min(Math.ceil(contentTotal / contentPerPage), p + 1))}
                          disabled={contentPage >= Math.ceil(contentTotal / contentPerPage)}
                          data-testid="content-next-page"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setContentPage(Math.ceil(contentTotal / contentPerPage))}
                          disabled={contentPage >= Math.ceil(contentTotal / contentPerPage)}
                          data-testid="content-last-page"
                        >
                          <ChevronsRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <Card className="bg-card border-none shadow-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <h3 className="font-serif text-xl font-semibold">All Users ({usersTotal})</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Show:</span>
                    <Select 
                      value={usersPerPage.toString()} 
                      onValueChange={(v) => { setUsersPerPage(parseInt(v)); setUsersPage(1); }}
                    >
                      <SelectTrigger className="w-20 h-8" data-testid="users-per-page-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {users.map((u) => (
                      <div key={u.user_id} className="flex items-center justify-between p-4 bg-secondary rounded-sm">
                        <div className="flex-1 min-w-0 mr-4">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{u.name}</p>
                            {u.is_admin && <Badge className="bg-destructive">Admin</Badge>}
                            {u.is_suspended && (
                              <Badge variant="outline" className="text-orange-500 border-orange-500">
                                <Ban className="w-3 h-3 mr-1" /> Suspended
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                          <p className="text-xs text-gold">{u.skin_points} SkinPoints</p>
                          {u.is_suspended && u.suspension_reason && (
                            <p className="text-xs text-orange-500 mt-1">Reason: {u.suspension_reason}</p>
                          )}
                        </div>
                        {u.email !== 'alghaflym@gmail.com' && (
                          <div className="flex items-center gap-2">
                            {u.is_suspended ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUnsuspendUser(u.user_id)}
                                className="text-green-600 hover:text-green-700"
                                data-testid={`unsuspend-${u.user_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Unsuspend
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSuspendDialog({ open: true, user: u })}
                                className="text-orange-500 hover:text-orange-600"
                                data-testid={`suspend-${u.user_id}`}
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                Suspend
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUser(u.user_id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Users Pagination */}
                  {usersTotal > 0 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        Showing {((usersPage - 1) * usersPerPage) + 1} - {Math.min(usersPage * usersPerPage, usersTotal)} of {usersTotal}
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setUsersPage(1)}
                          disabled={usersPage === 1}
                          data-testid="users-first-page"
                        >
                          <ChevronsLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                          disabled={usersPage === 1}
                          data-testid="users-prev-page"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="px-3 py-1 text-sm font-medium">
                          Page {usersPage} of {Math.ceil(usersTotal / usersPerPage)}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setUsersPage(p => Math.min(Math.ceil(usersTotal / usersPerPage), p + 1))}
                          disabled={usersPage >= Math.ceil(usersTotal / usersPerPage)}
                          data-testid="users-next-page"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setUsersPage(Math.ceil(usersTotal / usersPerPage))}
                          disabled={usersPage >= Math.ceil(usersTotal / usersPerPage)}
                          data-testid="users-last-page"
                        >
                          <ChevronsRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Create Content Tab */}
            <TabsContent value="create">
              <Card className="bg-card border-none shadow-card">
                <CardHeader>
                  <h3 className="font-serif text-xl font-semibold flex items-center gap-2">
                    <Plus className="w-5 h-5 text-gold" />
                    Create New Content
                  </h3>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateContent} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Content Type</Label>
                        <Select 
                          value={newContent.content_type} 
                          onValueChange={(v) => setNewContent({...newContent, content_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="news">News (Daily Drop)</SelectItem>
                            <SelectItem value="article">Article (Deep Dives)</SelectItem>
                            <SelectItem value="video">Video (Video Vault)</SelectItem>
                            <SelectItem value="knowledge">Knowledge Corner</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Input 
                          value={newContent.category}
                          onChange={(e) => setNewContent({...newContent, category: e.target.value})}
                          placeholder="e.g., Skincare Tips, Hair Science"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input 
                        value={newContent.title}
                        onChange={(e) => setNewContent({...newContent, title: e.target.value})}
                        placeholder="Content title"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Summary</Label>
                      <Textarea 
                        value={newContent.summary}
                        onChange={(e) => setNewContent({...newContent, summary: e.target.value})}
                        placeholder="Brief summary (shown in previews)"
                        rows={2}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Full Content</Label>
                      <div className="border rounded-md">
                        <ReactQuill
                          theme="snow"
                          value={newContent.content}
                          onChange={(value) => setNewContent({...newContent, content: value})}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Write your content here..."
                          className="bg-background"
                          style={{ minHeight: '200px' }}
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Image URL (optional - auto-assigned if empty)</Label>
                        <Input 
                          value={newContent.image_url}
                          onChange={(e) => setNewContent({...newContent, image_url: e.target.value})}
                          placeholder="https://..."
                        />
                      </div>
                      {newContent.content_type === 'video' && (
                        <div className="space-y-2">
                          <Label>Video URL (YouTube embed)</Label>
                          <Input 
                            value={newContent.video_url}
                            onChange={(e) => setNewContent({...newContent, video_url: e.target.value})}
                            placeholder="https://www.youtube.com/embed/..."
                          />
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Tags (comma-separated)</Label>
                      <Input 
                        value={newContent.tags}
                        onChange={(e) => setNewContent({...newContent, tags: e.target.value})}
                        placeholder="skincare, tips, moisturizer"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Read Time (minutes)</Label>
                        <Input 
                          type="number"
                          value={newContent.read_time}
                          onChange={(e) => setNewContent({...newContent, read_time: parseInt(e.target.value)})}
                          min={1}
                          max={60}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Points Reward</Label>
                        <Input 
                          type="number"
                          value={newContent.points_reward}
                          onChange={(e) => setNewContent({...newContent, points_reward: parseInt(e.target.value)})}
                          min={5}
                          max={100}
                        />
                      </div>
                    </div>

                    <Button type="submit" className="btn-gold w-full" disabled={creating}>
                      {creating ? 'Creating...' : 'Create Content'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Fetch Content Tab */}
            <TabsContent value="fetch">
              <Card className="bg-card border-none shadow-card">
                <CardHeader>
                  <h3 className="font-serif text-xl font-semibold flex items-center gap-2">
                    <Download className="w-5 h-5 text-gold" />
                    Fetch Content from Open Sources
                  </h3>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <p className="text-muted-foreground">
                      Import content from external sources to populate your platform with fresh articles and news.
                    </p>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Source</Label>
                        <Select value={fetchSource} onValueChange={setFetchSource}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pubmed">PubMed (Research Articles)</SelectItem>
                            <SelectItem value="rss">Beauty RSS Feeds</SelectItem>
                            <SelectItem value="curated">Curated Templates</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Topic</Label>
                        <Select value={fetchTopic} onValueChange={setFetchTopic}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="skincare">Skincare</SelectItem>
                            <SelectItem value="haircare">Haircare</SelectItem>
                            <SelectItem value="anti-aging">Anti-Aging</SelectItem>
                            <SelectItem value="acne">Acne</SelectItem>
                            <SelectItem value="retinol">Retinol</SelectItem>
                            <SelectItem value="vitamin c">Vitamin C</SelectItem>
                            <SelectItem value="sunscreen">Sunscreen</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button onClick={handleFetchContent} className="btn-gold w-full" disabled={fetching}>
                      {fetching ? 'Fetching...' : 'Fetch Content'}
                    </Button>

                    <div className="p-4 bg-secondary rounded-sm">
                      <h4 className="font-semibold mb-2">Available Sources:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• <strong>YouTube:</strong> Beauty and skincare video tutorials</li>
                        <li>• <strong>PubMed:</strong> Scientific research articles from NCBI</li>
                        <li>• <strong>RSS Feeds:</strong> Latest news from beauty publications (Allure, Byrdie, etc.)</li>
                        <li>• <strong>Curated:</strong> Pre-built content templates for quick population</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Automation Tab */}
            <TabsContent value="automation">
              <Card className="bg-card border-none shadow-card">
                <CardHeader>
                  <h3 className="font-serif text-xl font-semibold flex items-center gap-2">
                    <Zap className="w-5 h-5 text-gold" />
                    Content Auto-Fetch
                  </h3>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-sm">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">Auto-fetch is active</span>
                    </div>
                    <p className="text-sm text-green-600">
                      New content is automatically fetched from open sources every hour.
                    </p>
                  </div>

                  {autoFetchStatus && (
                    <>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="p-4 bg-secondary rounded-sm">
                          <p className="text-sm text-muted-foreground">Scheduler Status</p>
                          <p className="text-lg font-semibold flex items-center gap-2">
                            {autoFetchStatus.scheduler_running ? (
                              <><CheckCircle className="w-4 h-4 text-green-500" /> Running</>
                            ) : (
                              <><AlertTriangle className="w-4 h-4 text-orange-500" /> Stopped</>
                            )}
                          </p>
                        </div>
                        <div className="p-4 bg-secondary rounded-sm">
                          <p className="text-sm text-muted-foreground">Next Run</p>
                          <p className="text-lg font-semibold flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gold" />
                            {autoFetchStatus.jobs?.[0]?.next_run 
                              ? new Date(autoFetchStatus.jobs[0].next_run).toLocaleTimeString()
                              : 'N/A'}
                          </p>
                        </div>
                      </div>

                      {autoFetchStatus.recent_fetches?.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-3">Recent Auto-Fetches</h4>
                          <div className="space-y-2">
                            {autoFetchStatus.recent_fetches.map((fetch, i) => (
                              <div key={i} className="flex items-center justify-between p-3 bg-secondary rounded-sm text-sm">
                                <div>
                                  <span className="font-medium capitalize">{fetch.source}</span>
                                  <span className="text-muted-foreground"> - {fetch.topic}</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-gold">{fetch.count} items</span>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(fetch.timestamp).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <Button onClick={handleTriggerAutoFetch} variant="outline" className="w-full">
                    <Zap className="w-4 h-4 mr-2" />
                    Trigger Manual Fetch Now
                  </Button>

                  {/* Dynamic Content Sources */}
                  <div className="pt-6 border-t">
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-gold" />
                      Dynamic Content Sources
                    </h4>
                    <div className="space-y-4">
                      <div className="p-4 bg-secondary rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">YouTube Videos</span>
                          <Badge variant="outline" className="text-green-600 border-green-600">Active (Curated)</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          Fetches beauty and skincare videos from YouTube. Uses curated videos by default, or YouTube API if configured.
                        </p>
                        <p className="text-xs text-muted-foreground">
                          💡 To enable live YouTube search, add your YouTube Data API key in Admin Settings (coming soon) or set <code>YOUTUBE_API_KEY</code> environment variable.
                        </p>
                      </div>
                      
                      <div className="p-4 bg-secondary rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">RSS Feeds</span>
                          <Badge variant="outline" className="text-green-600 border-green-600">Active</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Fetches latest beauty news from: Allure, Byrdie, Paula's Choice, Self, Cosmopolitan
                        </p>
                      </div>
                      
                      <div className="p-4 bg-secondary rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">PubMed Research</span>
                          <Badge variant="outline" className="text-green-600 border-green-600">Active</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Fetches scientific research articles from NCBI PubMed database
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />

      {/* Suspend User Dialog */}
      <Dialog open={suspendDialog.open} onOpenChange={(open) => !open && setSuspendDialog({ open: false, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend User</DialogTitle>
            <DialogDescription>
              Are you sure you want to suspend {suspendDialog.user?.name}? They will not be able to log in until unsuspended.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Suspension Reason</Label>
              <Textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Reason for suspension (visible to user)"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleSuspendUser} 
              className="bg-orange-500 hover:bg-orange-600"
              disabled={actionLoading}
            >
              {actionLoading ? 'Suspending...' : 'Suspend User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Content Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, content: null })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-gold" />
              Edit Content
            </DialogTitle>
            <DialogDescription>
              Update the content details below. Changes will be saved immediately.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleEditContent} className="space-y-6 py-4">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select 
                  value={editContent.content_type} 
                  onValueChange={(v) => setEditContent({...editContent, content_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="news">News (Daily Drop)</SelectItem>
                    <SelectItem value="article">Article (Deep Dives)</SelectItem>
                    <SelectItem value="video">Video (Video Vault)</SelectItem>
                    <SelectItem value="knowledge">Knowledge Corner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input 
                  value={editContent.category}
                  onChange={(e) => setEditContent({...editContent, category: e.target.value})}
                  placeholder="e.g., Skincare Tips, Hair Science"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input 
                value={editContent.title}
                onChange={(e) => setEditContent({...editContent, title: e.target.value})}
                placeholder="Content title"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Summary</Label>
              <Textarea 
                value={editContent.summary}
                onChange={(e) => setEditContent({...editContent, summary: e.target.value})}
                placeholder="Brief summary (shown in previews)"
                rows={2}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Full Content</Label>
              <div className="border rounded-md">
                <ReactQuill
                  theme="snow"
                  value={editContent.content}
                  onChange={(value) => setEditContent({...editContent, content: value})}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your content here..."
                  className="bg-background"
                  style={{ minHeight: '200px' }}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Image URL (optional)</Label>
                <Input 
                  value={editContent.image_url}
                  onChange={(e) => setEditContent({...editContent, image_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>
              {editContent.content_type === 'video' && (
                <div className="space-y-2">
                  <Label>Video URL (YouTube embed)</Label>
                  <Input 
                    value={editContent.video_url}
                    onChange={(e) => setEditContent({...editContent, video_url: e.target.value})}
                    placeholder="https://www.youtube.com/embed/..."
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tags (comma-separated)</Label>
              <Input 
                value={editContent.tags}
                onChange={(e) => setEditContent({...editContent, tags: e.target.value})}
                placeholder="skincare, tips, moisturizer"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Read Time (minutes)</Label>
                <Input 
                  type="number"
                  value={editContent.read_time}
                  onChange={(e) => setEditContent({...editContent, read_time: parseInt(e.target.value) || 5})}
                  min={1}
                  max={60}
                />
              </div>
              <div className="space-y-2">
                <Label>Points Reward</Label>
                <Input 
                  type="number"
                  value={editContent.points_reward}
                  onChange={(e) => setEditContent({...editContent, points_reward: parseInt(e.target.value) || 10})}
                  min={5}
                  max={100}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setEditDialog({ open: false, content: null })}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="btn-gold" 
                disabled={editing}
                data-testid="save-content-btn"
              >
                {editing ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
